import i18n from '@/plugins/i18n'
import { saveUser } from '@/services/central-demarco-service'
import { centralDemarcoUserService } from '@/services/central-demarco-user-service'
import { toIdpUserPayloadCasing } from '@/utils/idp-payload-adapter'
import moment from 'moment'
import Swal from 'sweetalert2'

const DMCENTER_OIDD_FLAG = 'dmcenter_oidd'

const defaultCentralDemarcoUser = {
  referenceId: null,
  name: null,
  email: null,
  documentType: 'cpf',
  document: null,
  phone: null,
  phoneCountry: null,
  birthDate: null,
  termsOfUse: false,
  profile: null,
  type: null,
  position: null,
  worksAt: null,
  clients: [],
  isManager: false,
  isCycleManager: false,
  optin: false,
  createdByAdmin: false
}

const defaultBulkProgressConfig = {
  users: [],
  selectedCompanies: [],
  selectedUnitsMap: {},
  room: null
}

const state = {
  currentCentralDemarcoUser: defaultCentralDemarcoUser,
  initalUserType: null,
  bulkProgressConfig: defaultBulkProgressConfig
}

const getters = {
  isValidUserType: (state) => {
    if (!state.currentCentralDemarcoUser) return false

    return state.userTypeIsRequired
      ? state.currentCentralDemarcoUser.type
      : state.currentCentralDemarcoUser.type ||
      state.currentCentralDemarcoUser.profile === 'admin_sistema'
  },

  isValidUser: (state, getters) => {
    if (!state.currentCentralDemarcoUser) return false

    const user = state.currentCentralDemarcoUser

    return (
      user.name &&
      user.document &&
      user.email &&
      user.birthDate &&
      user.phone &&
      user.profile &&
      user.position &&
      user.worksAt &&
      getters.isValidUserType
    )
  },

  bulkUsers: (state) => state.bulkProgressConfig.users
}

const mutations = {
  SET_CENTRAL_DEMARCO_USER(state, payload) {
    state.currentCentralDemarcoUser = payload
  },
  SET_INITIAL_USER_TYPE(state, payload) {
    state.initalUserType = payload
  },
  SET_BULK_PROGRESS_CONFIG(state, payload) {
    state.bulkProgressConfig = payload
  },
  UPDATE_BULK_USER_PROGRESS(state, updatedUser) {
    const index = state.bulkProgressConfig.users.findIndex(user => user.referenceId === updatedUser.referenceId)
    if (index !== -1) {
      state.bulkProgressConfig.users.splice(index, 1, updatedUser)
    }
  }
}

const actions = {
  loadCentralDemarcoUser({ commit }, centralDemarcoUserData) {
    commit('SET_CENTRAL_DEMARCO_USER', centralDemarcoUserData)
    commit('SET_INITIAL_USER_TYPE', centralDemarcoUserData.type)
  },
  clearCentralDemarcoUser({ commit, rootState }) {
    const defCentralDemarcoUser = {
      referenceId: null,
      name: null,
      email: null,
      documentType: 'cpf',
      document: null,
      phone: null,
      phoneCountry: null,
      birthDate: null,
      profile: null,
      termsOfUse: false,
      type: null,
      position: null,
      worksAt: null,
      clients: [],
      isManager: false,
      isCycleManager: false,
      optin: false,
      createdByAdmin: false
    }
    commit('SET_CENTRAL_DEMARCO_USER', defCentralDemarcoUser)
    commit('SET_INITIAL_USER_TYPE', null)
  },
  async saveUser({ rootState, dispatch, commit }, config = {}) {
    const { currentCentralDemarcoUser, initalUserType } = rootState.currentCentralDemarcoUser
    const { userProfile, isMaster } = rootState.user

    const user = await dispatch('saveOrUpdateUserOnCentralDemarco', config)

    const referenceId = user.data?.data?.id ?? user.data?.Data?.Id
    let { clients } = currentCentralDemarcoUser
    if (currentCentralDemarcoUser._id && referenceId) {
      const { data: freshUser } = await centralDemarcoUserService.getByReferenceId(referenceId)
      clients = freshUser?.clients || clients
    }

    const updatedUser = {
      ...currentCentralDemarcoUser,
      referenceId,
      clients,
      createdByAdmin: userProfile === 'admin_sistema'
    }

    if (updatedUser.profile === 'admin_sistema' && !isMaster) {
      updatedUser.type = initalUserType
    } else {
      commit('SET_INITIAL_USER_TYPE', updatedUser.type)
    }

    const { data } = await centralDemarcoUserService.save(updatedUser, config)

    commit('SET_CENTRAL_DEMARCO_USER', {
      ...data, birthDate: moment
        .utc(updatedUser.birthDate)
        .format('YYYY-MM-DD')
        .toString()
    })

    return data
  },
  async saveOrUpdateUserOnCentralDemarco({ rootState, dispatch, rootGetters }, config = {}) {
    const { currentCentralDemarcoUser } = rootState.currentCentralDemarcoUser

    const cpf = currentCentralDemarcoUser.document
      ? currentCentralDemarcoUser.document.replace(/\D/g, '')
      : null

    const userPayload = {
      passwordDefinition: 1,
      phoneNumber: currentCentralDemarcoUser.phone.replace(/\D/g, ''),
      name: currentCentralDemarcoUser.name,
      email: currentCentralDemarcoUser.email.toLowerCase(),
      clients: 'client',
      units: '5feb70150af0190001d8c440',
      active: 1,
      profile: currentCentralDemarcoUser.profile,
      document: cpf,
      birthDate: currentCentralDemarcoUser.birthDate,
      claims: {
        dmpeople_grupo_id: currentCentralDemarcoUser.profile
      }
    }

    if (rootState.systemFeatures.flags[DMCENTER_OIDD_FLAG] === undefined) {
      await dispatch('systemFeatures/fetchFlag', DMCENTER_OIDD_FLAG, { root: true })
    }
    const dmcenterOidd = rootGetters['systemFeatures/isEnabled'](DMCENTER_OIDD_FLAG)
    const adaptedPayload = toIdpUserPayloadCasing(userPayload, dmcenterOidd)

    const localeMap = {
      en: 'en-US',
      es: 'es-AR'
    }
    const locale = localeMap[rootState.user.locale] || rootState.user.locale

    return saveUser(currentCentralDemarcoUser.referenceId, adaptedPayload, locale, config)
  },

  setBulkProgressConfig({ commit }, configData) {
    commit('SET_BULK_PROGRESS_CONFIG', configData)
  },

  async SOCKET_saveUserClientConfiguration({ commit, state, dispatch }, data) {
    const { room, config, error } = data
    const { bulkProgressConfig } = state

    if (room !== bulkProgressConfig.room) return

    const { referenceId } = config
    const userIndex = bulkProgressConfig.users.findIndex(user => user.referenceId === referenceId)
    if (userIndex === -1) return

    const totalSteps = bulkProgressConfig.selectedCompanies.length
    const currentUser = bulkProgressConfig.users[userIndex]

    const updatedUser = {
      ...currentUser,
      progress: currentUser.progress + 1
    }

    if (error) {
      updatedUser.status = 'error'
    }

    if (updatedUser.progress >= totalSteps && updatedUser.status !== 'error') {
      updatedUser.status = 'ok'
    }

    commit('UPDATE_BULK_USER_PROGRESS', updatedUser)

    const allDone = bulkProgressConfig.users.every(user => user.progress >= totalSteps)

    if (allDone) {
      const hasError = bulkProgressConfig.users.some(user => user.status === 'error')

      if (hasError) {
        await Swal.fire({
          icon: 'error',
          title: i18n.t('importante'),
          text: i18n.t('erroaoprocessaradicoessolicitadas'),
          showCancelButton: false,
          showConfirmButton: true,
          confirmButtonColor: '#53933a'
        }).then(async (result) => {
          if (result.isConfirmed) {
            await dispatch('showModal')
          } else {
            commit('SET_BULK_PROGRESS_CONFIG', defaultBulkProgressConfig)
          }
        })

        return
      }

      await Swal.fire({
        icon: 'success',
        title: i18n.t('asadicoesforamsalvas'),
        text: i18n.t('cliquenobotaookparavisulizar'),
        showCancelButton: false,
        showConfirmButton: true,
        confirmButtonColor: '#53933a'
      }).then(async (result) => {
        if (result.isConfirmed) {
          await dispatch('showModal')
        } else {
          commit('SET_BULK_PROGRESS_CONFIG', defaultBulkProgressConfig)
        }
      })
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
